import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Compass, Pencil, Trash2, CalendarDays, TableIcon, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { TourBookingsCalendar } from "./TourBookingsCalendar";

const statusLabels: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const paymentMethodLabels: Record<string, string> = {
  cash: "Cash",
  paypal: "PayPal",
  stripe: "Card",
};

function EditBookingDialog({ row, tours, onSave }: { row: any; tours: any[]; onSave: (id: string, data: Record<string, any>) => Promise<void> }) {
  const [form, setForm] = useState({
    tour_id: row.tour_id || "",
    customer_name: row.customer_name || "",
    customer_email: row.customer_email || "",
    customer_phone: row.customer_phone || "",
    excursion_date: row.excursion_date || "",
    schedule: row.schedule || "",
    adults: row.adults || 1,
    children: row.children || 0,
    total_price: row.total_price || 0,
    pickup_point: row.pickup_point || "",
    payment_method: row.payment_method || "cash",
    payment_status: row.payment_status || "unpaid",
    status: row.status || "pending",
    comments: row.comments || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(row.id, {
      ...form,
      tour_id: form.tour_id || null,
      schedule: form.schedule || null,
      pickup_point: form.pickup_point || null,
      comments: form.comments || null,
      adults: Number(form.adults),
      children: Number(form.children),
      total_price: Number(form.total_price),
    });
    setSaving(false);
  };

  const set = (field: string, value: any) => setForm((p) => ({ ...p, [field]: value }));

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-xs">Tour</Label>
          <Select value={form.tour_id} onValueChange={(v) => set("tour_id", v)}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select a tour..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— No Tour —</SelectItem>
              {tours.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Customer Name</Label>
          <Input className="mt-1" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Phone</Label>
          <Input className="mt-1" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Email</Label>
          <Input className="mt-1" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
        </div>

        <div>
          <Label className="text-xs">Excursion Date</Label>
          <Input className="mt-1" type="date" value={form.excursion_date} onChange={(e) => set("excursion_date", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Schedule</Label>
          <Input className="mt-1" value={form.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="e.g. 09:00" />
        </div>

        <div>
          <Label className="text-xs">Adults</Label>
          <Input className="mt-1" type="number" min={0} value={form.adults} onChange={(e) => set("adults", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Children</Label>
          <Input className="mt-1" type="number" min={0} value={form.children} onChange={(e) => set("children", e.target.value)} />
        </div>

        <div>
          <Label className="text-xs">Total Price</Label>
          <Input className="mt-1" type="number" min={0} step="0.01" value={form.total_price} onChange={(e) => set("total_price", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Pickup Point</Label>
          <Input className="mt-1" value={form.pickup_point} onChange={(e) => set("pickup_point", e.target.value)} />
        </div>

        <div>
          <Label className="text-xs">Payment Method</Label>
          <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="paypal">PayPal</SelectItem>
              <SelectItem value="stripe">Card</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Payment Status</Label>
          <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2">
          <Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(statusLabels).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2">
          <Label className="text-xs">Comments</Label>
          <Textarea className="mt-1" value={form.comments} onChange={(e) => set("comments", e.target.value)} rows={2} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
        <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
      </div>
    </div>
  );
}

export function TourBookingsSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [tours, setTours] = useState<any[]>([]);
  const { toast } = useToast();

  const load = async () => {
    const [bookingsRes, toursRes] = await Promise.all([
      supabase.from("tour_bookings").select("*, tours(title, slug)").order("created_at", { ascending: false }),
      supabase.from("tours").select("id, title").order("title"),
    ]);
    if (bookingsRes.error) {
      toast({ title: "Error", description: bookingsRes.error.message, variant: "destructive" });
      return;
    }
    setRows(bookingsRes.data || []);
    setTours(toursRes.data || []);
  };

  useEffect(() => { load(); }, []);

  const updateField = async (id: string, field: string, value: string) => {
    const { error } = await supabase.from("tour_bookings").update({ [field]: value } as any).eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    load();
  };

  const updateBooking = async (id: string, data: Record<string, any>) => {
    const updateData = { ...data };
    if (updateData.tour_id === "none") updateData.tour_id = null;
    const { error } = await supabase.from("tour_bookings").update(updateData as any).eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Updated", description: "Tour booking has been updated." });
    load();
  };

  const deleteBooking = async (id: string) => {
    const { error } = await supabase.from("tour_bookings").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Deleted", description: "Tour booking has been removed." });
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Tour Bookings</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Manage tour reservations <Badge variant="secondary" className="ml-2">{rows.length}</Badge>
          </p>
        </div>
      </div>

      <Tabs defaultValue="table" className="w-full">
        <TabsList>
          <TabsTrigger value="table" className="gap-1.5">
            <TableIcon className="h-4 w-4" /> Table
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-1.5">
            <CalendarDays className="h-4 w-4" /> Calendar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table">
          <Card>
            <CardContent className="p-0">
              {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Compass className="h-10 w-10 mb-3 opacity-40" />
                  <p>No tour bookings yet</p>
                </div>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Tour</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Guests</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="font-mono text-xs font-bold text-primary">
                            {row.booking_code || "—"}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">{row.customer_name}</div>
                            <div className="text-xs text-muted-foreground">{row.customer_phone}</div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm max-w-[150px] truncate">{(row.tours as any)?.title || "— Sin tour —"}</div>
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.excursion_date ? format(new Date(row.excursion_date + "T12:00:00"), "MMM d, yyyy") : "—"}
                            {row.schedule && <div className="text-muted-foreground">{row.schedule}</div>}
                          </TableCell>
                          <TableCell className="text-sm">
                            {`${row.adults}A${row.children > 0 ? ` + ${row.children}C` : ""}`}
                          </TableCell>
                          <TableCell className="font-semibold">${row.total_price}</TableCell>
                          <TableCell>
                            <Select value={row.payment_status} onValueChange={(v) => updateField(row.id, "payment_status", v)}>
                              <SelectTrigger className="h-7 w-[100px] text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unpaid">Unpaid</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select value={row.status} onValueChange={(v) => updateField(row.id, "status", v)}>
                              <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusLabels).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {row.booking_code && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                  <a href={`/voucher/${row.booking_code}`} target="_blank" rel="noopener noreferrer" title="View Voucher">
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </Button>
                              )}

                              {/* Edit Dialog */}
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7"><Pencil className="h-4 w-4" /></Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg">
                                  <DialogHeader>
                                    <DialogTitle>Edit Tour Booking — {row.booking_code || row.id.slice(0, 8)}</DialogTitle>
                                  </DialogHeader>
                                  <EditBookingDialog row={row} tours={tours} onSave={updateBooking} />
                                </DialogContent>
                              </Dialog>

                              {/* Delete */}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Tour Booking?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This action cannot be undone. The booking for {row.customer_name} will be permanently removed.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteBooking(row.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          <TourBookingsCalendar rows={rows} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
